import { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { multiply, round, divide, add, subtract } from 'mathjs';
import { Box, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import { dataGridPremiumColumns } from '../../utils/helper';
import {
  FormErrorUI,
  InputUI,
  AutoCompleteSelectUI,
  AddButtonUi,
  DatagridPremiumUi,
  DatePickerUI,
  CheckboxUI,
} from '../Interface';
import useConfig from '../../context/ConfigContext';
import { formatCurrency } from '../../utils/currencyHelper';
import priceListService from '../../service/priceList.service';
import { DocumentDirection } from '../../config/DocumentDirection';
import { DocStatus, DocStatusList } from '../../config/document';
import { InvManagementMethod, ItemGroupType } from '../../config/itemGroup';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';
import useGenerateJokerCols from '../../hooks/useGenerateJokerCols';
import discountRuleService from '../../service/discountRule.service';
import { getLineEditableFields, isCellEditable } from '../../utils/DocHelper';
import dayjs from 'dayjs';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';
import useParam from '../../context/ParamContext';
import ItemDialog from '../Item/ItemDialog';
import masterDataService from '../../service/masterData.service';
import vatCodeService from '../../service/vatCode.service';

const EditToolbar = (props) => {
  const { disabled, push, handleClick } = props;

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={() => handleClick(push)} />
    </GridToolbarContainer>
  );
};

const DocumentLine = ({
  disabled,
  defaultField,
  autocompleteOptions,
  autocompleteData,
  documentData,
  selectedCompanyState,
  viewField,
  datagridIdentifiers,
  currentDocType,
  jokerLineFields,
  selectedValues,
  batchLineState,
  setNoteDialogState,
  docTypes,
  setAutoCompleteData,
  setAutoCompleteOptions,
}) => {
  const apiRef = useGridApiRef();
  const focusedFieldRef = useRef(null);
  const { params } = useParam();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const { config, getConfig } = useConfig();
  const { selectedCompany } = selectedCompanyState;
  const { setBatchLine } = batchLineState;

  const [itemDialogLineId, setItemDialogLineId] = useState(null);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);

  const [selectedDocCur, setSelectedDocCur] = useState(null);

  useEffect(() => {
    const docCur = autocompleteData.currency.find((c) => c.code === values.currencyCode);
    setSelectedDocCur(docCur);
  }, [values.currencyCode, autocompleteData.currency]);

  const docTotal = () => {
    const lines = values.docLine ?? [];
    //mathjs
    const totalNetPrice = lines.reduce((acc, line) => acc + parseFloat(line.netPrice ?? 0), 0);
    const totalVatValue = lines.reduce((acc, line) => acc + parseFloat(line.vatValue ?? 0), 0);
    const totalGrossPrice = lines.reduce((acc, line) => acc + parseFloat(line.grossPrice ?? 0), 0);
    const totalNetPriceInOwnCur = lines.reduce((acc, line) => acc + parseFloat(line.netPriceInOwnCur ?? 0), 0);
    const totalVatValueInOwnCur = lines.reduce((acc, line) => acc + parseFloat(line.vatValueInOwnCur ?? 0), 0);
    const totalGrossPriceInOwnCur = lines.reduce((acc, line) => acc + parseFloat(line.grossPriceInOwnCur ?? 0), 0);

    // ha a saját pénznem és a dokumentum pénzneme megegyezik, akkor a saját bruttó legyen ugyan az mint a kerekítés után a bizonylat pénznemében

    setFieldValue('sumInDocCur', totalGrossPrice);
    setFieldValue('netInDocCur', totalNetPrice);
    setFieldValue('VATInDocCur', totalVatValue);
    //setFieldValue('roundingInDocCur', );

    setFieldValue('sumInOwnCur', totalGrossPriceInOwnCur);
    setFieldValue('netInOwnCur', totalNetPriceInOwnCur);
    setFieldValue('VATInOwnCur', totalVatValueInOwnCur);
    //setFieldValue('roundingInOwnCur', );
  };

  useEffect(() => {
    docTotal();
    recalculateAllLineFields();
  }, [values.docLine, values.exchangeRate]);

  useEffect(() => {
    if (!Object.keys(config).length) getConfig();
  }, [getConfig, config]);

  const focusFirstEditableCell = (rowId) => {
    const editableFields = getLineEditableFields(apiRef, rowId, { currentDocType, selectedValues });
    const fieldToFocus = editableFields[0] ?? 'itemCode';

    focusedFieldRef.current = fieldToFocus;
    apiRef.current.startRowEditMode({ id: rowId });
    apiRef.current.setCellFocus(rowId, fieldToFocus);
    focusEditorInput(rowId, fieldToFocus);
  };

  const createNewLineData = () => {
    const findedProject = autocompleteData?.project.find((p) => p.projectId === values.projectId);
    const findedItem = autocompleteData.item.find((u) => u.itemCode === defaultField.itemCode);
    const findedQtyUnit = autocompleteData.qtyUnit.find((u) => u.id === findedItem?.quantityUnitId);

    const resolvedVatCode = selectedCompany
      ? documentData.direction === DocumentDirection.INCOMING
        ? selectedCompany?.supplierVatCode
        : selectedCompany?.customerVatCode
      : null;

    const findedVat = autocompleteData.vatCode.find((u) => u.code === resolvedVatCode);

    return {
      ...defaultField,
      id: uuidv4(),
      isNew: true,
      vatCode: resolvedVatCode,
      vatPercent: findedVat?.percentage ?? 0,
      projectId: values.projectId,
      projectOrderNumber: findedProject?.orderNumber ?? '',
      costCenterCode: values.costCenterCode,
      whsCode: values.whsCode ?? defaultField.whsCode,
      itemName: findedItem ? findedItem.itemName : '',
      weightChangeNumber: findedItem ? findedItem.weightChangeNumber : null,
      quantityUnitId: findedItem ? findedItem.quantityUnitId : '',
      quantityUnitCode: findedQtyUnit ? findedQtyUnit.code : '',
      quantityUnitName: findedQtyUnit ? findedQtyUnit.name : '',
      landedCost: false,
    };
  };

  const handleClick = async (push) => {
    const newField = createNewLineData();

    await push(newField);
    focusFirstEditableCell(newField.id);

    const fieldsToValidate = [
      `docLine.${values.docLine.length ?? 0}.itemCode`,
      `docLine.${values.docLine.length ?? 0}.itemCodeInBp`,
      `docLine.${values.docLine.length ?? 0}.qty`,
      `docLine.${values.docLine.length ?? 0}.quantityUnitId`,
      `docLine.${values.docLine.length ?? 0}.whsCode`,
      `docLine.${values.docLine.length ?? 0}.toWhsCode`,
      `docLine.${values.docLine.length ?? 0}.unitPrice`,
      `docLine.${values.docLine.length ?? 0}.vatCode`,
      `docLine.${values.docLine.length ?? 0}.netPrice`,
      `docLine.${values.docLine.length ?? 0}.grossPrice`,
      `docLine.${values.docLine.length ?? 0}.dueDate`,
      `docLine.${values.docLine.length ?? 0}.weight`,
      `docLine.${values.docLine.length ?? 0}.projectId`,
      `docLine.${values.docLine.length ?? 0}.costCenterCode`,
      `docLine.${values.docLine.length ?? 0}.itemReference`,
      `docLine.${values.docLine.length ?? 0}.internalNote`,
      `docLine.${values.docLine.length ?? 0}.bpNote`,
    ];

    for (const field of fieldsToValidate) {
      validateField(field);
    }

    return defaultField.id;
  };

  const handleItemChange = async (valueIndex, value, fromPaste = false) => {
    setFieldValue(`docLine.${valueIndex}.itemCode`, value);

    const selectedItem = autocompleteData.item.find((i) => i.itemCode === value);
    if (selectedItem) {
      setFieldValue(`docLine.${valueIndex}.itemName`, selectedItem?.itemName ?? null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitId`, selectedItem?.quantityUnitId ?? null);
      const findedQtyUnit = autocompleteData.qtyUnit.find((u) => u.id === selectedItem?.quantityUnitId);
      setFieldValue(`docLine.${valueIndex}.quantityUnitCode`, findedQtyUnit?.code ?? null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitName`, findedQtyUnit?.name ?? null);
      setFieldValue(`docLine.${valueIndex}.weightChangeNumber`, selectedItem?.weight ?? null);
      setFieldValue(`docLine.${valueIndex}.landedCost`, Boolean(selectedItem?.landedCost));

      calculateWeight(valueIndex, values.docLine[valueIndex].qty, selectedItem?.weight ?? null);
      let pl = {};
      if (!fromPaste) {
        if (values.priceListId) {
          pl = await priceListService.getItemPrice({
            itemCode: value,
            priceListId: values.priceListId,
          });
          if (pl?.price) {
            const price = parseFloat(pl.price);
            setFieldValue(`docLine.${valueIndex}.unitPrice`, price);
            setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, price);
            calculatePrice(valueIndex, values.docLine[valueIndex].qty, pl.price, values.docLine[valueIndex].vatPercent);
          }
        }

        const discount = await discountRuleService.getDiscount({
          itemCode: value,
          companyId: values.companyId ?? null,
          priceListId: values.priceListId ?? null,
          date: values.docDate,
        });

        if (discount) {
          setFieldValue(`docLine.${valueIndex}.discountPercent`, discount.percent);
          setFieldValue(`docLine.${valueIndex}.ruleId`, discount.id);
          if (pl?.price) {
            const price = parseFloat(pl.price);
            const unitPrice = calculateDiscountValue(discount.percent, price);
            setFieldValue(`docLine.${valueIndex}.unitPrice`, unitPrice);
            calculatePrice(
              valueIndex,
              values.docLine[valueIndex].qty,
              unitPrice,
              values.docLine[valueIndex].vatPercent
            );
          }
        }

        // Áfa kód lekérése

        if (selectedCompany) {
          const resolvedVatCode = await vatCodeService.getVatCodeToDoc({
            documentTypeId: documentData.documentTypeId,
            itemCode: value,
            companyId: values.companyId ?? null,
          });

          if (Object.keys(resolvedVatCode).length > 0) {
            // Update Formik values
            setFieldValue(`docLine.${valueIndex}.vatCode`, resolvedVatCode);
            const findedVat = autocompleteData.vatCode.find((u) => u.code === resolvedVatCode);
            setFieldValue(`docLine.${valueIndex}.vatPercent`, findedVat?.percentage ?? null);

            calculatePrice(
              valueIndex,
              values.docLine[valueIndex].qty,
              values.docLine[valueIndex].unitPrice,
              findedVat?.percentage ?? 0
            );

            const grossUnitPrice = round(
              calculateGrossPrice(values.docLine[valueIndex].unitPrice ?? 0, findedVat?.percentage ?? 0),
              selectedDocCur?.amountRounding ?? 0
            );

            setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossUnitPrice);
          }
        }
      }
    } else {
      setFieldValue(`docLine.${valueIndex}.itemName`, null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitId`, null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitCode`, null);
      setFieldValue(`docLine.${valueIndex}.quantityUnitName`, null);
      setFieldValue(`docLine.${valueIndex}.weightChangeNumber`, null);
      setFieldValue(`docLine.${valueIndex}.unitPrice`, null);
      setFieldValue(`docLine.${valueIndex}.baseUnitPrice`, null);
      setFieldValue(`docLine.${valueIndex}.landedCost`, false);
    }
  };

  const calculateGrossPrice = (netPrice, vatPercent) => {
    return multiply(netPrice, add(1, divide(vatPercent, 100)));
  };

  const calculateNetPrice = (grossPrice, vatPercent) => {
    return divide(multiply(grossPrice, 100), add(100, vatPercent));
  };

  const handlePriceChange = (valueIndex, value) => {
    const roundedValue = round(parseFloat(value ?? 0), selectedDocCur?.amountRounding ?? 0);
    setFieldValue(`docLine.${valueIndex}.unitPrice`, roundedValue ?? '');

    setFieldValue(`docLine.${valueIndex}.discountPercent`, null);
    setFieldValue(`docLine.${valueIndex}.ruleId`, null);

    const vatPercent = values.docLine[valueIndex].vatPercent ?? 0;

    calculatePrice(valueIndex, values.docLine[valueIndex].qty, roundedValue ?? '0', vatPercent);

    const grossUnitPrice = round(
      calculateGrossPrice(roundedValue ?? 0, vatPercent),
      selectedDocCur?.amountRounding ?? 0
    );

    setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossUnitPrice);
  };

  const handleGrossPriceChange = (valueIndex, value) => {
    const roundedValue = round(parseFloat(value ?? 0), selectedDocCur?.amountRounding ?? 0);
    setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, roundedValue ?? '');
    const vatPercent = values.docLine[valueIndex].vatPercent ?? 0;
    const netUnitPrice = round(calculateNetPrice(roundedValue ?? 0, vatPercent), selectedDocCur?.amountRounding ?? 0);
    setFieldValue(`docLine.${valueIndex}.unitPrice`, netUnitPrice);

    setFieldValue(`docLine.${valueIndex}.discountPercent`, null);
    setFieldValue(`docLine.${valueIndex}.ruleId`, null);

    calculatePrice(valueIndex, values.docLine[valueIndex].qty, netUnitPrice, vatPercent);
  };

  const handleQtyChange = (valueIndex, value) => {
    const inputValue = parseFloat(value);
    const cplQty = parseFloat(values.docLine?.[valueIndex]?.cplQty ?? 0);

    if (inputValue < cplQty) {
      addNotification({
        content: 'A mennyiség nem lehet kisebb a lezárt mennyiségnél!',
        type: NotificationType.ERROR,
      });

      return;
    }

    setFieldValue(`docLine.${valueIndex}.qty`, value ?? '');

    if (isNaN(inputValue)) return;

    const rawOpnQty = subtract(inputValue, cplQty);
    const newOpnQty = rawOpnQty <= 0 ? 0 : round(rawOpnQty, 6);

    setFieldValue(`docLine.${valueIndex}.opnQty`, newOpnQty);

    if (newOpnQty === 0) setFieldValue(`docLine.${valueIndex}.lineStatus`, DocStatus.CLOSED);

    calculatePrice(
      valueIndex,
      value ?? '0',
      values.docLine[valueIndex].unitPrice,
      values.docLine[valueIndex].vatPercent
    );

    calculateWeight(valueIndex, value ?? '0', values.docLine[valueIndex].weightChangeNumber);
  };

  const getValueIndex = (values, id) => {
    return values?.docLine?.findIndex((rc) => rc.id === id);
  };

  const jokerColumns = useGenerateJokerCols(jokerLineFields, 'docLine', getValueIndex);

  const recalculateAllLineFields = () => {
    values?.docLine?.forEach((line, index) => {
      const qty = line.qty ?? 0;
      const unitPrice = line.unitPrice ?? 0;
      const vatPercent = line.vatPercent ?? 0;
      const weightChangeNumber = line.weightChangeNumber ?? 0;

      calculatePrice(index, qty, unitPrice, vatPercent);
      calculateWeight(index, qty, weightChangeNumber);
    });
  };

  const calculateDiscountValue = (discountPercent, baseUnitPrice) => {
    const base = parseFloat(baseUnitPrice ?? 0);
    const percent = parseFloat(discountPercent ?? 0);

    if (!base) return 0;
    if (!percent) return base;

    const discountValue = multiply(base, divide(percent, 100));
    return parseFloat(subtract(base, discountValue));
  };

  const handleItemDialogOpen = (rowId) => {
    setItemDialogLineId(rowId);
    setItemDialogOpen(true);
  };

  const selectItemFromDialog = async (itemCode) => {
    setItemDialogOpen(false);

    if (!itemCode || itemDialogLineId == null) {
      setItemDialogLineId(null);
      return;
    }

    const index = getValueIndex(values, itemDialogLineId);
    setItemDialogLineId(null);

    if (index === -1 || !values.docLine?.[index]) return;

    // autocompletedata, autocompleteoptions frissítése, ha nincs benne a kiválasztott cikk

    const findedItem = autocompleteData.item.find((i) => i.itemCode === itemCode);

    if (!findedItem) {
      const itm = await masterDataService.itemsFromTableToFormByItemCode(itemCode);

      if (!itm) {
        addNotification({
          content: 'Hiba történt a cikk adatainak lekérésekor!',
          type: NotificationType.ERROR,
        });
      }

      const itemData = itm[0];

      const newAutocompleteData = { ...autocompleteData };
      newAutocompleteData.item.push(itemData);
      setAutoCompleteData(newAutocompleteData);

      const newAutocompleteOptions = { ...autocompleteOptions };
      newAutocompleteOptions.item.push({ label: itemData.itemCode, value: itemData.itemCode });
      setAutoCompleteOptions(newAutocompleteOptions);
    }

    handleItemChange(index, itemCode);

    const currentQty = values.docLine?.[index]?.qty;
    if (!currentQty || Number(currentQty) === 0) {
      handleQtyChange(index, 1);
    }
  };

  const columns = useMemo(
    () => (remove) =>
      dataGridPremiumColumns([
        {
          field: 'itemCode',
          headerName: 'Cikkszám',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.item.find(
              (u) => u.value === values.docLine?.[valueIndex]?.itemCode
            );

            return findedItem?.value;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.item.find(
              (u) => u.value === values.docLine?.[valueIndex]?.itemCode
            );
            return (
              <>
                <Field
                  name={`docLine.${valueIndex}.itemCode`}
                  component={AutoCompleteSelectUI}
                  helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.itemCode} />}
                  value={findedItem}
                  onChange={async (_e, newVal) => {
                    const val = newVal?.value ?? null;
                    handleItemChange(valueIndex, val);

                    data.api.setEditCellValue({
                      id: data.id,
                      field: data.field,
                      value: val,
                    });
                  }}
                  dataset={autocompleteOptions?.item}
                  onBlur={async () => await validateField(`docLine.${valueIndex}.itemCode`)}
                />
                <Box>
                  <AddButtonUi onClick={() => handleItemDialogOpen(data.id)} />
                </Box>
              </>
            );
          },
        },
        {
          field: 'itemName',
          headerName: 'Cikknév',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return values.docLine?.[valueIndex]?.itemName ?? '';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.itemName`}
                as={InputUI}
                variant="standard"
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.itemName} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.itemName`, e.target.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.itemName`)}
              />
            );
          },
        },
        {
          field: 'itemCodeInBp',
          headerName: 'ÜP cikkszám',
          type: 'string',
          editable: true,
          sortable: false,

          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.itemCodeInBp`}
                as={InputUI}
                type="text"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.itemCodeInBp} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.itemCodeInBp`, e.target.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.itemCodeInBp`)}
              />
            );
          },
        },
        {
          field: 'qty',
          headerName: 'Mennyiség',
          type: 'number',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const item = autocompleteData.item.find((i) => i.itemCode === values.docLine?.[valueIndex]?.itemCode);

            return (
              <>
                <Field
                  name={`docLine.${valueIndex}.qty`}
                  type="number"
                  variant="standard"
                  as={InputUI}
                  fullWidth
                  helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.qty} />}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    handleQtyChange(valueIndex, rawValue);

                    data.api.setEditCellValue({
                      id: data.id,
                      field: data.field,
                      value: rawValue,
                    });
                  }}
                  onBlur={async () => await validateField(`docLine.${valueIndex}.qty`)}
                />
                {item?.invManagementMethod === InvManagementMethod.BATCH && currentDocType.inventoryAccounting && (
                  <IconButton
                    aria-label="edit"
                    onClick={() => {
                      setBatchLine({ ...data.row, selectedLineIndex: valueIndex });
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </>
            );
          },
        },
        {
          field: 'quantityUnitCode',
          headerName: 'ME',
          type: 'string',
          editable: false,
          sortable: false,
        },
        {
          field: 'opnQty',
          headerName: 'Nyitott',
          type: 'number',
          editable: false,
          sortable: false,
        },
        {
          field: 'cplQty',
          headerName: 'Lezárt',
          type: 'number',
          editable: false,
          sortable: false,
        },
        {
          field: 'whsCode',
          headerName: 'Raktár',
          type: 'string',
          editable: true,
          sortable: false,
          aggregable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.warehouse.find((u) => {
              const docLineCode = values.docLine?.[valueIndex]?.whsCode;
              return u.value === (docLineCode || values.whsCode);
            });

            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.warehouse.find((u) => {
              const docLineCode = values.docLine?.[valueIndex]?.whsCode;
              return u.value === (docLineCode || values.whsCode);
            });
            return (
              <Field
                name={`docLine.${valueIndex}.whsCode`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.whsCode} />}
                //selectedValue={values.docLine?.[valueIndex]?.whsCode}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.whsCode`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                }}
                dataset={autocompleteOptions?.warehouse}
                onBlur={async () => await validateField(`docLine.${valueIndex}.whsCode`)}
              />
            );
          },
        },
        {
          field: 'toWhsCode',
          headerName: 'Fogadó raktár',
          type: 'string',
          editable: true,
          sortable: false,
          aggregable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.warehouse.find((u) => {
              const docLineCode = values.docLine?.[valueIndex]?.toWhsCode;
              return u.value === (docLineCode || values.toWhsCode);
            });

            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.warehouse.find((u) => {
              const docLineCode = values.docLine?.[valueIndex]?.toWhsCode;
              return u.value === (docLineCode || values.toWhsCode);
            });
            return (
              <Field
                name={`docLine.${valueIndex}.toWhsCode`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.toWhsCode} />}
                //selectedValue={values.docLine?.[valueIndex]?.toWhsCode}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.toWhsCode`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                }}
                dataset={autocompleteOptions?.warehouse}
                onBlur={async () => await validateField(`docLine.${valueIndex}.toWhsCode`)}
              />
            );
          },
        },
        {
          field: 'unitPrice',
          headerName: 'Egységár',
          type: 'number',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return values.docLine?.[valueIndex]?.unitPrice
              ? formatCurrency(values.docLine?.[valueIndex]?.unitPrice, selectedDocCur?.amountRounding ?? 0)
              : ' ';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.unitPrice`}
                type="number"
                variant="standard"
                as={InputUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.unitPrice} />}
                onChange={(e) => {
                  const value = e.target.value;
                  handlePriceChange(valueIndex, e.target.value ?? '');

                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.unitPrice`)}
              />
            );
          },
        },

        {
          field: 'grossUnitPrice',
          headerName: 'Bruttó egységár',
          type: 'number',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return values.docLine?.[valueIndex]?.grossUnitPrice
              ? formatCurrency(values.docLine?.[valueIndex]?.grossUnitPrice ?? '', selectedDocCur?.amountRounding ?? 0)
              : '';
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.grossUnitPrice`}
                type="number"
                variant="standard"
                as={InputUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.grossUnitPrice} />}
                onChange={(e) => {
                  const value = e.target.value;
                  handleGrossPriceChange(valueIndex, e.target.value ?? '');

                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.grossUnitPrice`)}
              />
            );
          },
        },

        {
          field: 'discountPercent',
          headerName: 'Kedvezmény %',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? `${formatCurrency(params)} %` : '';
          },
        },

        {
          field: 'baseUnitPrice',
          headerName: 'Alap egységár',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
          },
        },

        /*{
        field: 'baseGrossUnitPrice',
        headerName: 'Alap bruttó egységár',
        type: 'number',
        editable: false,
        sortable: false,
        valueGetter: (value) => parseFloat(value),
        valueFormatter: (params) => {
          return formatCurrency(params, selectedDocCur?.amountRounding ?? 0);
        },
      },*/

        {
          field: 'currencyCode',
          headerName: 'Ár pénznem',
          type: 'string',
          editable: false,
          sortable: false,
          renderCell: () => {
            const currFromPl = autocompleteOptions?.priceList.find((u) => u.id === values.priceListId);
            return currFromPl?.currencyCode ?? values.currencyCode;
          },
        },

        {
          field: 'vatCode',
          headerName: 'Áfakód',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.vatCode.find((u) => {
              const docLineCode = values.docLine?.[valueIndex]?.vatCode;
              return u.value === (docLineCode || values.vatCode);
            });

            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.vatCode.find((u) => {
              const docLineCode = values.docLine?.[valueIndex]?.vatCode;
              return u.value === (docLineCode || values.vatCode);
            });
            return (
              <Field
                name={`docLine.${valueIndex}.vatCode`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.vatCode} />}
                selectedValue={values.docLine?.[valueIndex]?.vatCode}
                selectedLabelValue={findedItem?.label}
                //value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.vatCode`, newVal?.value ?? null);
                  const findedVat = autocompleteData.vatCode.find((u) => u.code === newVal?.value);
                  setFieldValue(`docLine.${valueIndex}.vatPercent`, findedVat?.percentage ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                  calculatePrice(
                    valueIndex,
                    values.docLine[valueIndex].qty,
                    values.docLine[valueIndex].unitPrice,
                    findedVat?.percentage ?? '0'
                  );

                  const grossPrice = round(
                    calculateGrossPrice(values.docLine[valueIndex].unitPrice, findedVat?.percentage ?? '0'),
                    selectedDocCur?.amountRounding ?? 0
                  );
                  setFieldValue(`docLine.${valueIndex}.grossUnitPrice`, grossPrice);
                }}
                dataset={autocompleteOptions?.vatCode}
                onBlur={async () => await validateField(`docLine.${valueIndex}.vatCode`)}
              />
            );
          },
        },

        {
          field: 'vatPercent',
          headerName: 'Áfa %',
          type: 'number',
          editable: false,
          sortable: false,
        },

        {
          field: 'netPrice',
          headerName: 'Nettó érték',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
          },
        },

        {
          field: 'vatValue',
          headerName: 'Áfa érték',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
          },
        },

        {
          field: 'grossPrice',
          headerName: 'Bruttó érték',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
          },
        },

        {
          field: 'netPriceInOwnCur',
          headerName: 'Nettó saját pénznemben',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
          },
        },

        {
          field: 'vatValueInOwnCur',
          headerName: 'Áfa saját pénznemben',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
          },
        },

        {
          field: 'grossPriceInOwnCur',
          headerName: 'Bruttó saját pénznemben',
          type: 'number',
          editable: false,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          valueFormatter: (params) => {
            return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
          },
        },
        ...(documentData.additionalCost
          ? [
              {
                field: 'finalUnitCost',
                headerName: 'Költséggel növelt egységár saját pénznemben',
                type: 'number',
                editable: false,
                sortable: false,
                valueGetter: (value) => (value ? parseFloat(value) : ''),
                valueFormatter: (params) => {
                  return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
                },
              },
              {
                field: 'finalLineValue',
                headerName: 'Költséggel növelt nettó saját pénznemben',
                type: 'number',
                editable: false,
                sortable: false,
                valueGetter: (value) => (value ? parseFloat(value) : ''),
                valueFormatter: (params) => {
                  return params ? formatCurrency(params, selectedDocCur?.amountRounding ?? 0) : '';
                },
              },
              {
                field: 'landedCost',
                headerName: 'Mellékköltség',
                type: 'boolean',
                editable: true,
                sortable: false,
                valueGetter: (value, row) => {
                  const valueIndex = getValueIndex(values, row.id);
                  return values.docLine?.[valueIndex]?.landedCost;
                },
                renderEditCell: (data) => {
                  const valueIndex = getValueIndex(values, data.id);
                  const findedItem = autocompleteData.item.find(
                    (i) => i.itemCode === values.docLine?.[valueIndex]?.itemCode
                  );
                  return (
                    <Field
                      name={`docLine.${valueIndex}.landedCost`}
                      type="checkbox"
                      component={CheckboxUI}
                      helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.landedCost} />}
                      checked={values.docLine?.[valueIndex]?.landedCost ?? false}
                      variant="standard"
                      disabled={findedItem?.type !== 'SERVICE'}
                      onChange={(e) => {
                        setFieldValue(`docLine.${valueIndex}.landedCost`, e.target.checked);
                        data.api.setEditCellValue({
                          id: data.id,
                          field: data.field,
                          value: e.target.checked,
                        });
                      }}
                    />
                  );
                },
              },
            ]
          : []),

        {
          field: 'weightChangeNumber',
          headerName: 'Súly váltó',
          type: 'number',
          editable: false,
          sortable: false,
        },

        {
          field: 'weight',
          headerName: 'Súly',
          type: 'number',
          editable: true,
          sortable: false,
          valueGetter: (value) => parseFloat(value),
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`docLine.${valueIndex}.weight`}
                type="number"
                variant="standard"
                as={InputUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.weight} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.weight`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.weight`)}
              />
            );
          },
        },

        {
          field: 'projectId',
          headerName: 'Projekt',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.project.find(
              (u) => u.value === values.docLine?.[valueIndex]?.projectId
            );
            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.project.find(
              (u) => u.value === values.docLine?.[valueIndex]?.projectId
            );
            return (
              <Field
                name={`docLine.${valueIndex}.projectId`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.projectId} />}
                selectedValue={values.docLine?.[valueIndex]?.projectId}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.projectId`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });

                  const project = autocompleteData.project.find((p) => p.projectId === newVal?.value);
                  setFieldValue(`docLine.${valueIndex}.projectOrderNumber`, project?.orderNumber ?? '');
                }}
                dataset={autocompleteOptions?.project}
                onBlur={async () => await validateField(`docLine.${valueIndex}.projectId`)}
              />
            );
          },
        },

        {
          field: 'projectOrderNumber',
          headerName: getProjectOrderNumberName(params),
          type: 'string',
          editable: false,
          sortable: false,
        },

        {
          field: 'costCenterCode',
          headerName: 'Költséghely',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.costCenter.find(
              (u) => u.value === values.docLine?.[valueIndex]?.costCenterCode
            );
            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions?.costCenter.find(
              (u) => u.value === values.docLine?.[valueIndex]?.costCenterCode
            );
            return (
              <Field
                name={`docLine.${valueIndex}.costCenterCode`}
                as={AutoCompleteSelectUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.costCenterCode} />}
                selectedValue={values.docLine?.[valueIndex]?.costCenterCode}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`docLine.${valueIndex}.costCenterCode`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                }}
                dataset={autocompleteOptions?.costCenter}
                onBlur={async () => await validateField(`docLine.${valueIndex}.costCenterCode`)}
              />
            );
          },
        },

        {
          field: 'itemReference',
          headerName: 'Tétel hivatkozás',
          type: 'string',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.itemReference`}
                as={InputUI}
                type="text"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.itemReference} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.itemReference`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.itemReference`)}
              />
            );
          },
        },

        {
          field: 'internalNote',
          headerName: 'Belső megjegyzés',
          type: 'string',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.internalNote`}
                as={InputUI}
                type="text"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.internalNote} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.internalNote`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.internalNote`)}
              />
            );
          },
        },
        {
          field: 'bpNote',
          headerName: 'Üp megjegyzés',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (params) => {
            const value = params?.row?.bpNote ?? '';
            return (
              <Tooltip title={value}>
                <Box
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {value}
                </Box>
              </Tooltip>
            );
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.bpNote`}
                as={InputUI}
                type="text"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.bpNote} />}
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.bpNote`, e.target.value ?? '');
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.value,
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.bpNote`)}
              />
            );
          },
        },
        {
          field: 'lineStatus',
          headerName: 'Státusz',
          type: 'string',
          editable: false,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return DocStatusList.find((i) => i.value === values.docLine?.[valueIndex]?.lineStatus)?.label;
          },
        },
        {
          field: 'baseDocType',
          headerName: 'Alapbizonylat neve',
          type: 'string',
          editable: false,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return docTypes.find((i) => i.documentTypeId === values.docLine?.[valueIndex]?.baseDocType)?.name;
          },
        },
        {
          field: 'baseDocnum',
          headerName: 'Alapbizonylat száma',
          type: 'string',
          editable: false,
          sortable: false,
        },
        {
          field: 'baseLine',
          headerName: 'Alapbizonylat sora',
          type: 'string',
          editable: false,
          sortable: false,
        },
        {
          field: 'dueDate',
          headerName: 'Határidő',
          type: 'date',
          sortable: false,
          filterable: false,
          editable: true,
          valueGetter: (_data, row) => {
            return new Date(row.dueDate);
          },
          renderCell: (data) => {
            return dayjs(data.row.dueDate).format('YYYY.MM.DD');
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.dueDate`}
                component={DatePickerUI}
                type="date"
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.dueDate} />}
                value={values.docLine?.[valueIndex]?.dueDate}
                onChange={(date) => {
                  setFieldValue(`docLine.${valueIndex}.dueDate`, date);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: new Date(date),
                  });
                }}
                onBlur={async () => await validateField(`docLine.${valueIndex}.dueDate`)}
              />
            );
          },
        },
        {
          field: 'isBillable',
          headerName: 'Számlázandó',
          type: 'boolean',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`docLine.${valueIndex}.isBillable`}
                type="checkbox"
                component={CheckboxUI}
                helperText={<FormErrorUI message={errors?.docLine?.[valueIndex]?.isBillable} />}
                checked={values.docLine?.[valueIndex]?.isBillable ?? false}
                variant="standard"
                onChange={(e) => {
                  setFieldValue(`docLine.${valueIndex}.isBillable`, e.target.checked);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.checked,
                  });
                }}
              />
            );
          },
        },
        {
          field: 'actions',
          type: 'actions',
          headerName: '',
          cellClassName: 'actions',
          sortable: false,
          getActions: ({ id, row }) => {
            const foundedItemType = autocompleteData?.item?.find((item) => item.itemCode === row.itemCode)?.type;
            return [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => {
                  const valueIndex = getValueIndex(values, id);
                  return remove(valueIndex);
                }}
                color="inherit"
                disabled={
                  disabled ||
                  row.deletable === false ||
                  (currentDocType.inventoryAccounting &&
                    foundedItemType !== ItemGroupType.SERVICE &&
                    !row.isNew &&
                    selectedValues.docStatus !== DocStatus.PLANNED)
                }
              />,
            ];
          },
        },
        ...jokerColumns,
      ]),
    [
      values,
      errors,
      autocompleteData,
      autocompleteOptions,
      currentDocType,
      selectedValues,
      docTypes,
      config,
      documentData,
    ]
  );

  const calculatePrice = (valueIndex, _qty, _unitPrice, _vatPercent) => {
    const qty = parseFloat(_qty ?? '0');
    const unitPrice = parseFloat(_unitPrice ?? '0');
    const vatPercent = parseFloat(_vatPercent ?? '0');

    const ownCur = autocompleteData.currency.find((c) => c.code === config.currencyCode);

    const netPrice = round(multiply(unitPrice, qty), selectedDocCur?.amountRounding ?? 0);
    const vatValue =
      vatPercent > 0 ? round(multiply(netPrice, divide(vatPercent, 100)), selectedDocCur?.amountRounding ?? 0) : 0;
    const grossPrice = round(add(netPrice, vatValue), selectedDocCur?.amountRounding ?? 0);

    setFieldValue(`docLine.${valueIndex}.netPrice`, netPrice);
    setFieldValue(`docLine.${valueIndex}.vatValue`, vatValue);
    setFieldValue(`docLine.${valueIndex}.grossPrice`, grossPrice);

    const exchangeRate = Number(values.exchangeRate);
    const netPriceInOwnCur = round(
      multiply(netPrice, exchangeRate > 0 ? exchangeRate : 1),
      ownCur?.amountRounding ?? 0
    );
    const vatValueInOwnCur = round(
      multiply(vatValue, exchangeRate > 0 ? exchangeRate : 1),
      ownCur?.amountRounding ?? 0
    );
    const grossPriceInOwnCur = round(
      multiply(grossPrice, exchangeRate > 0 ? exchangeRate : 1),
      ownCur?.amountRounding ?? 0
    );
    setFieldValue(`docLine.${valueIndex}.netPriceInOwnCur`, netPriceInOwnCur);
    setFieldValue(`docLine.${valueIndex}.vatValueInOwnCur`, vatValueInOwnCur);
    setFieldValue(`docLine.${valueIndex}.grossPriceInOwnCur`, grossPriceInOwnCur);

    setTimeout(() => {
      docTotal();
    }, 800);
  };

  const calculateWeight = (valueIndex, _qty, _weightChangeNumber) => {
    const qty = parseFloat(_qty ?? '0');
    const weightChangeNumber = parseFloat(_weightChangeNumber ?? '0');

    const weight = round(multiply(qty, weightChangeNumber), 2);
    setFieldValue(`docLine.${valueIndex}.weight`, weight);

    apiRef.current.setEditCellValue({
      id: values.docLine[valueIndex].id,
      field: 'weight',
      value: weight,
    });

    setTimeout(() => {
      docTotal();
    }, 800);
  };

  const focusEditorInput = (id, field, { select = true } = {}) => {
    apiRef.current.setCellFocus(id, field);

    requestAnimationFrame(() => {
      const cellEl = apiRef.current.getCellElement?.(id, field);
      if (!cellEl) return;

      const editorInput = cellEl.querySelector('input, textarea, [contenteditable="true"], [tabindex="0"], button');

      if (editorInput) {
        editorInput.focus();
        if (select && typeof editorInput.select === 'function') {
          try {
            editorInput.select();
          } catch {}
        }
      }
    });
  };

  const handleArrowKeyNavigation = async (params, event, push) => {
    const key = event.key;
    const isArrowKey = key === 'ArrowDown' || key === 'ArrowUp';

    if (!isArrowKey || !event.ctrlKey) return;

    event.preventDefault();

    const rowId = params.id;
    if (apiRef.current.getRowMode(rowId) !== GridRowModes.Edit) return;

    apiRef.current.stopRowEditMode({ id: rowId });

    const rowIndex = getValueIndex(values, rowId);
    const offset = key === 'ArrowDown' ? 1 : -1;
    let nextIndex = rowIndex + offset;

    while (
      nextIndex >= 0 &&
      nextIndex < values.docLine.length &&
      values.docLine[nextIndex].lineStatus !== DocStatus.OPENED
    ) {
      nextIndex += offset;
    }

    const nextRow = values.docLine[nextIndex];

    if (key === 'ArrowDown' && !nextRow) {
      await handleClick(push);
      return;
    }

    if (!nextRow) return;

    if (apiRef.current.getRowMode(nextRow.id) === GridRowModes.View) {
      apiRef.current.startRowEditMode({ id: nextRow.id });
    }

    focusedFieldRef.current = params.field;
    focusEditorInput(nextRow.id, params.field);
  };

  const handleTabKeyNavigation = (params, event) => {
    const isTabOrEnter = event.key === 'Tab' || event.key === 'Enter';
    if (!isTabOrEnter) return;

    const rowId = params.id;

    const editableFields = getLineEditableFields(apiRef, rowId, { currentDocType, selectedValues });

    const currentField = params.field;
    const currentIdx = editableFields.indexOf(currentField);
    const dir = event.shiftKey ? -1 : 1;
    const nextIdx = currentIdx + dir;

    if (nextIdx < 0 || nextIdx >= editableFields.length) return;

    event.preventDefault();
    event.defaultMuiPrevented = true;

    const nextField = editableFields[nextIdx];

    focusedFieldRef.current = nextField;
    focusEditorInput(rowId, nextField);
  };

  const [updateLine, setUpdateLine] = useState(null);

  useEffect(() => {
    if (!updateLine) return;

    handleUpdatePastedLine(updateLine);
    setUpdateLine(null);
  }, [updateLine]);

  const handleUpdatePastedLine = async (data) => {
    const { itemCode, qty, unitPrice, newIndex } = data;
    await handleItemChange(newIndex, itemCode);
    handleQtyChange(newIndex, qty);
    handlePriceChange(newIndex, unitPrice ?? '0');
  };

  const [pendingUpdates, setPendingUpdates] = useState([]);

  useEffect(() => {
    if (pendingUpdates.length === 0) return;

    const process = async () => {
      const [next, ...rest] = pendingUpdates;
      const { itemCode, qty, unitPrice, newIndex } = next;

      await handleItemChange(newIndex, itemCode, true);
      handleQtyChange(newIndex, qty);
      handlePriceChange(newIndex, unitPrice ?? '0');

      setPendingUpdates(rest);
    };

    process();
  }, [pendingUpdates]);

  const handleClipboardPaste = async (params, insert) => {
    const { data } = params;

    // kiinduló index a meglévő sorok száma alapján
    const startIndex = values.docLine.length;
    // csak a sikeresen felvett sorok növelik ezt
    let successOffset = 0;

    for (const row of data) {
      const [itemCode, qty, whsCode, unitPrice, internalNote] = row;

      const item = autocompleteData.item.find((i) => i.itemCode === itemCode);
      if (!item) {
        addNotification({
          content: `A(z) ${itemCode} cikk nem létezik !`,
          type: NotificationType.ERROR,
        });
        continue;
      }

      if (isNaN(parseFloat(qty))) {
        addNotification({
          content: `A mennyiség megadása kötelező a(z) ${itemCode} cikkhez!`,
          type: NotificationType.ERROR,
        });
        continue;
      }

      const newLine = {
        ...createNewLineData(),
        ...(whsCode && { whsCode }),
        ...(internalNote && { internalNote }),
      };

      const newIndex = startIndex + successOffset;
      successOffset += 1;

      insert(newIndex, newLine);

      setPendingUpdates((prev) => [...prev, { itemCode, qty, unitPrice, newIndex }]);
    }
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="docLine">
            {({ remove, push, insert }) => (
              <>
                <DatagridPremiumUi
                  key={datagridIdentifiers.docLine}
                  apiRef={apiRef}
                  datagridIdentifier={datagridIdentifiers.docLine}
                  height={480}
                  columns={columns(remove)}
                  rows={values?.docLine ?? []}
                  editMode="row"
                  slots={{
                    toolbar: EditToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      disabled,
                      push,
                      defaultField,
                      apiRef,
                      documentData,
                      selectedCompany,
                      autocompleteData,
                      handleClick,
                    },
                  }}
                  onCellDoubleClick={(params, event) => {
                    event.defaultMuiPrevented = true;
                    if (viewField) return;
                    if (params.field === 'bpNote') {
                      event.defaultMuiPrevented = true;
                      const valueIndex = getValueIndex(values, params.row.id);
                      setNoteDialogState({ open: true, valueIndex });
                      return;
                    }
                  }}
                  onCellClick={(params, event) => {
                    if (viewField) return;
                    if (params.row.lineStatus !== DocStatus.OPENED) return;
                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onRowEditStop={() => {
                    focusedFieldRef.current = null;
                  }}
                  onCellKeyDown={(params, event) => {
                    handleArrowKeyNavigation(params, event, push);
                    handleTabKeyNavigation(params, event, push);
                  }}
                  isCellEditable={isCellEditable({ currentDocType, selectedValues })}
                  onClipboardPasteStart={(params) => handleClipboardPaste(params, insert)}
                />
              </>
            )}
          </FieldArray>
        </div>
      </div>
      {/* ItemDialog külön, nem a FieldArray render-függvényében */}
      <ItemDialog showDialog={itemDialogOpen} onClose={selectItemFromDialog} />
    </div>
  );
};

export default DocumentLine;
